import type { ToastProps } from './Toast';

export type ToastVariant = NonNullable<ToastProps['variant']>;
export type ToastInput = Omit<ToastProps, 'id' | 'onClose'> & { id?: string };

// Simple module-level pub/sub store
let toasts: ToastProps[] = [];
const listeners = new Set<(items: ToastProps[]) => void>();

function notify() {
  // Avoid downlevelIteration requirement by using Array.from
  Array.from(listeners).forEach((l) => l(toasts));
}

export function subscribe(listener: (items: ToastProps[]) => void) {
  listeners.add(listener);
  listener(toasts);
  return () => { listeners.delete(listener); };
}

export function remove(id: string) {
  toasts = toasts.filter(t => t.id !== id);
  notify();
}

function push(input: ToastInput, variant: ToastVariant): string {
  const id = input.id || Math.random().toString(36).slice(2, 9);
  const toast: ToastProps = {
    id,
    description: input.description,
    title: input.title,
    duration: input.duration ?? 5000,
    action: input.action,
    variant,
  };
  toasts = [...toasts, toast];
  notify();
  return id;
}

export const toastBus = {
  subscribe,
  show: (description: string, variant: ToastVariant = 'info', opts?: Partial<Omit<ToastInput, 'description'>>) =>
    push({ ...(opts || {}), description }, variant),
  info: (description: string, opts?: Partial<Omit<ToastInput, 'description'>>) => push({ ...(opts || {}), description }, 'info'),
  success: (description: string, opts?: Partial<Omit<ToastInput, 'description'>>) => push({ ...(opts || {}), description }, 'success'),
  warning: (description: string, opts?: Partial<Omit<ToastInput, 'description'>>) => push({ ...(opts || {}), description }, 'warning'),
  error: (description: string, opts?: Partial<Omit<ToastInput, 'description'>>) => push({ ...(opts || {}), description }, 'error'),
  remove,
};
